import axios, { type AxiosInstance, type AxiosResponse } from "axios";
import { acceptHMRUpdate, defineStore } from "pinia";
import { useAuthStore } from "./auth";

/** Response types */

type PublishStateType = 0 | 1 | 2;

export interface IBase {
    id: number;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

export interface ISector extends IBase {
    name: string;
}

export interface ISegment extends IBase {
    name: string;
}

export interface ILanguage extends IBase {
    name: string;
    code: string;
}

export interface IAreaTag extends IBase {
    name: string;
    market_id: number;
}

export interface ILogin {
    name: string;
    token: string;
}

export interface IUser {
    id: number;
    name: string;
    email: string;
    role_id: number;
    email_verified_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface IMe {
    user: IUser;
}

export interface ITextValue {
    language_id: number;
    language: ILanguage;
    dirty?: boolean;
}

export interface IText extends IBase, ITextValue {
    name: string;
    description: string;
    publish_state: PublishStateType;
}

export interface IAudioFile extends IBase {
    file_url: string;
    filename: string;
    language_id: number;
    name: string;
    training_program_id: number;
}

export interface IAudioFileUploadPayload {
    file: File;
    name: string;
    training_program_id: number;
    language_id: number;
}

export interface IAudioFileUpdatePayload {
    id: number;
    name: string;
}

export interface IChairErrorLog extends IBase {
    chair_id: number;
    error_code: string;
    // Fault name looked up from the error code, null for codes missing from
    // the production guide's list. Only appended on the chair details endpoint.
    definition?: string | null;
    fixed_at: string | null;
}

export interface IEventLog {
    id: number;
    timestamp: string;
    io_event: string;
    event_id: string | null;
    command: string | null;
    value: string | null;
}

export interface IChairStatus {
    name: string;
    description: string;
    severity: number;
}

export interface IChairServiceData {
    total_seconds: number;
    x_motor_seconds: number;
    y_motor_seconds: number;
    z_motor_seconds: number;
    heat_pad_seconds: number;
    leg_tilt_seconds: number;
    lifetime_seconds: number;
    tilt_motor_seconds: number;
    leg_massage_seconds: number;
    left_massage_seconds: number;
    neck_massage_seconds: number;
    right_massage_seconds: number;
}

export interface IChairService extends IBase {
    chair_id: number;
    service_data: IChairServiceData;
}

export interface IChair extends IBase {
    customer_id: number | null;
    market_id: number | null;
    seller_id: number | null;
    name: string | null;
    location: string | null;
    serial: string;
    firmware_version: string;
    app_version: string;
    is_staging: boolean;
    device: string;
    android_version: string;
    last_online: string;
    last_massage_run_at: string;
    battery_level: number;
    area_tags: IAreaTag[];
    customer: ICustomer[];
    seller: ISeller[];
    market?: IMarket | null;
    chair_error_logs: IChairErrorLog[];
    status: IChairStatus;
    is_online: boolean;
    state: number;
    service: IChairService | null;
    active_version_override: string | null;
}

export interface IStatus {
    name: string;
    count: number;
}

export interface IBlock extends IBase {
    name: string;
    description: string;
    duration: number;
}

export interface IBlockGroup extends IBase {
    name: string;
    duration: number;
    selected_block_id: number;
    block_ids: number[];
    is_pause?: boolean;
    start_pos?: number;
    end_pos?: number;
}

export interface IMassageProgramText extends IText {
    massage_program_id: number;
}

export interface IMassageProgram extends IBase {
    internal_name: string;
    blocks: any[];
    neck_blocks: any[];
    leg_blocks: any[];
    block_groups: IBlockGroup[];
    neck_block_groups: IBlockGroup[];
    leg_block_groups: IBlockGroup[];
    massage_program_texts: IMassageProgramText[];
    training_programs: any[];
}

export interface ITrainingProgramText extends IText {
    training_program_id: number;
}

export interface ITrainingProgram extends IBase {
    internal_name: string;
    audio_files: IAudioFile[];
    markets: any[];
    massage_programs: any[];
    training_categories: any[];
    training_program_texts: ITrainingProgramText[];
}

export interface ITrainingCategoryText extends IText {
    training_category_id: number;
    type: string;
}

export interface ITrainingCategory extends IBase {
    internal_name: string;
    training_programs: ITrainingProgram[];
    training_category_texts: ITrainingCategoryText[];
    image_filename: string | null;
    image_url: string;
}

export interface IStaticPageText extends ITextValue {
    static_page_id: number;
    data: any;
}

export interface IStaticPage extends IBase {
    slug: string;
    name: string;
    static_page_texts: IStaticPageText[];
}

export interface IUiComponentText extends ITextValue {
    ui_component_id: number;
    text: string;
}

export interface IUiComponent extends IBase {
    slug: string;
    name: string;
    type: string;
    ui_component_texts: IUiComponentText[];
}

export interface IMarket extends IBase {
    name: string;
    parent_market_id: number | null;
    chairs: IChair[];
    area_tags: IAreaTag[];
}

export interface IPortalCustomer {
    id: number;
    name: string;
    /** The customer this portal customer is linked to, or null when free. */
    customer_id?: number | null;
    customer_name?: string | null;
    /** Portal access is switched off in Upsales, so nobody there can log in. */
    disabled?: boolean;
    /** 0 until someone accepts an invite. The Upsales client webhook creates
     *  the portal customer as soon as the company is granted access. */
    user_count?: number;
}

export interface ICustomer extends IBase {
    name: string;
    sector_id: number | null;
    segment_id: number | null;
    sector: ISector;
    segment: ISegment;
    market_id: number;
    portal_customer_id: number | null;
    portal_customer?: IPortalCustomer | null;
}

/** Visibility of an item on the market, mirroring the app's exclusion rules. */
export type ContentTreeState = "visible" | "test_only" | "hidden";

/** Publish/existence status of a single language for an item. */
export type ContentTreeTextStatus =
    | "published"
    | "test_only"
    | "unpublished"
    | "missing";

/** Machine readable reason an item is (not) visible; translated in the view. */
export interface IContentTreeReason {
    code: string;
    languages: string[];
}

/** Per-language status for a training program text (has a publish state). */
export interface IContentTreeTextStatus {
    language_id: number;
    code: string;
    name: string;
    status: ContentTreeTextStatus;
    publish_state: PublishStateType | null;
}

/** Per-language presence for category / massage texts (no publish state). */
export interface IContentTreePresence {
    language_id: number;
    code: string;
    name: string;
    present: boolean;
}

export interface IContentTreeStateSummary {
    visible: number;
    test_only: number;
    hidden: number;
}

export interface IContentTreeMassageProgram {
    id: number;
    internal_name: string;
    is_test: boolean;
    state: ContentTreeState;
    reasons: IContentTreeReason[];
    language_statuses: IContentTreePresence[];
}

export interface IContentTreeProgram {
    id: number;
    internal_name: string;
    name: string;
    state: ContentTreeState;
    reasons: IContentTreeReason[];
    gate_a: boolean;
    text_statuses: IContentTreeTextStatus[];
    massage_summary: IContentTreeStateSummary;
    massage_programs: IContentTreeMassageProgram[];
}

export interface IContentTreeCategory {
    id: number | null;
    internal_name: string | null;
    name: string | null;
    uncategorized: boolean;
    state: ContentTreeState;
    reasons: IContentTreeReason[];
    text_statuses: IContentTreePresence[];
    program_summary: IContentTreeStateSummary;
    programs: IContentTreeProgram[];
}

export interface IMarketContentTree {
    market: {
        id: number;
        name: string;
        languages: {
            id: number;
            code: string;
            name: string;
            is_primary: boolean;
        }[];
        summary: {
            categories: IContentTreeStateSummary;
            programs: IContentTreeStateSummary;
        };
    };
    categories: IContentTreeCategory[];
}

export interface IRole extends IBase {
    name: string;
    label: string;
    level: number;
}

export interface IUser {
    id: number;
    name: string;
    email: string;
    role: IRole;
}

export interface ISetting {
    id: number;
    key: string;
    value: string;
    created_at: string;
    updated_at: string;
}

export interface IInvite {
    email: string;
    invite_id: number;
    token: string;
    role: IRole;
    url: string;
}

export interface ISeller extends IBase {
    name: string;
    market_id: number;
}

/** Payload types */
export interface IMarketPayload {
    name: string;
    area_tags: IAreaTag[];
    languages: { id: number; is_primary: boolean | number }[];
    parent_market_id?: number;
}

export interface ISendInvitePayload {
    email: string;
    role_id: number;
}

export interface IAcceptInvitePayload {
    name: string;
    password: string;
    password_confirmation: string;
}

export interface IApiData<T = any> {
    success: boolean;
    message: string;
    data?: T;
}

export interface IApiResponse<T = any> extends AxiosResponse<IApiData<T>> {
}

export interface IApiState {
    token: string | null;
    api: AxiosInstance;
}

export interface IApiActions {
    login: (email: string, password: string) => Promise<IApiResponse<ILogin>>;
    logout: (everywhere?: boolean) => Promise<IApiResponse<[]>>;
    me: () => Promise<IApiResponse<IMe>>;
    blocks: () => Promise<IApiResponse<IBlock[]>>;
    createBlock: () => Promise<IApiResponse<any>>;
    updateBlock: () => Promise<IApiResponse<any>>;
    deleteBlock: () => Promise<IApiResponse<any>>;
    blockGroups: () => Promise<IApiResponse<IBlockGroup[]>>;
    massagePrograms: () => Promise<IApiResponse<IMassageProgram[]>>;
    getMassageProgram: (id: string) => Promise<IApiResponse<IMassageProgram>>;
    createMassageProgram: (payload: any) => Promise<IApiResponse<any>>;
    updateMassageProgram: (
        id: string,
        payload: any
    ) => Promise<IApiResponse<any>>;
    deleteMassageProgram: (id: string) => Promise<IApiResponse<any>>;
    trainingPrograms: () => Promise<IApiResponse<ITrainingProgram[]>>;
    getTrainingProgram: (id: string) => Promise<IApiResponse<ITrainingProgram>>;
    createTrainingProgram: (payload: any) => Promise<IApiResponse<any>>;
    updateTrainingProgram: (
        id: string,
        payload: any
    ) => Promise<IApiResponse<any>>;
    deleteTrainingProgram: (id: string) => Promise<IApiResponse<any>>;
    trainingCategories: () => Promise<IApiResponse<ITrainingCategory[]>>;
    getTrainingCategory: (id: string) => Promise<IApiResponse<ITrainingCategory>>;
    createTrainingCategory: (payload: any) => Promise<IApiResponse<any>>;
    updateTrainingCategory: (
        id: string,
        payload: any
    ) => Promise<IApiResponse<any>>;
    deleteTrainingCategory: (id: string) => Promise<IApiResponse<any>>;
    addTrainingCategoryImage: (
        id: string | number,
        payload: any
    ) => Promise<IApiResponse<any>>;
    deleteTrainingCategoryImage: (
        id: string | number
    ) => Promise<IApiResponse<any>>;
    markets: () => Promise<IApiResponse<IMarket[]>>;
    getMarket: (id: string) => Promise<IApiResponse<IMarket>>;
    getMarketContentTree: (
        id: string
    ) => Promise<IApiResponse<IMarketContentTree>>;
    createMarket: (payload: IMarketPayload) => Promise<IApiResponse<IMarket>>;
    updateMarket: (
        id: string,
        payload: IMarketPayload
    ) => Promise<IApiResponse<IMarket>>;
    deleteMarket: (id: string) => Promise<IApiResponse<any>>;
    chairs: () => Promise<
        IApiResponse<{ chairs: IChair[]; statuses: IStatus[] }>
    >;
    getChair: (id: string) => Promise<IApiResponse<IChair>>;
    updateChair: (id: string, payload: any) => Promise<IApiResponse<any>>;
    deleteChair: (id: string) => Promise<IApiResponse<any>>;
    setChairErrorFixed: (id: number) => Promise<IApiResponse<IChairErrorLog>>;
    getChairEventLogs: (id: string | number) => Promise<IApiResponse<IEventLog[]>>;
    deleteChairEventLogs: (id: string | number) => Promise<IApiResponse<any>>;
    staticPages: () => Promise<IApiResponse<IStaticPage[]>>;
    getStaticPage: (slug: string) => Promise<IApiResponse<IStaticPage>>;
    updateStaticPage: (slug: string, payload: any) => Promise<IApiResponse<any>>;
    uiComponents: () => Promise<IApiResponse<IUiComponent[]>>;
    getUiComponent: (slug: string) => Promise<IApiResponse<IUiComponent>>;
    updateUiComponent: (slug: string, payload: any) => Promise<IApiResponse<any>>;
    languages: () => Promise<IApiResponse<ILanguage[]>>;
    createLanguage: () => Promise<IApiResponse<any>>;
    updateLanguage: () => Promise<IApiResponse<any>>;
    deleteLanguage: () => Promise<IApiResponse<any>>;
    createAreaTag: (payload: any) => Promise<IApiResponse<IAreaTag>>;
    deleteAreaTag: (id: number) => Promise<IApiResponse<IAreaTag>>;
    sellers: () => Promise<IApiResponse<ISeller[]>>;
    getSeller: (id: string) => Promise<IApiResponse<ISeller>>;
    createSeller: (payload: any) => Promise<IApiResponse<ISeller>>;
    updateSeller: (id: string, payload: any) => Promise<IApiResponse<ISeller>>;
    deleteSeller: (id: string) => Promise<IApiResponse<any>>;
    customers: () => Promise<IApiResponse<ICustomer[]>>;
    getCustomer: (id: string) => Promise<IApiResponse<ICustomer>>;
    createCustomer: (payload: any) => Promise<IApiResponse<ICustomer>>;
    updateCustomer: (
        id: string,
        payload: any
    ) => Promise<IApiResponse<ICustomer>>;
    deleteCustomer: (id: string) => Promise<IApiResponse<any>>;
    portalCustomers: () => Promise<IApiResponse<IPortalCustomer[]>>;
    uploadAudio: (payload: any) => Promise<IApiResponse<IAudioFile>>;
    updateAudio: (
        payload: IAudioFileUpdatePayload
    ) => Promise<IApiResponse<IAudioFile>>;
    deleteAudio: (id: number) => Promise<IApiResponse<IAudioFile>>;
    sectors: () => Promise<IApiResponse<ISector[]>>;
    createSector: (payload: any) => Promise<IApiResponse<ISector>>;
    segments: () => Promise<IApiResponse<ISegment[]>>;
    createSegment: (payload: any) => Promise<IApiResponse<ISegment>>;
    sendInvite: (payload: ISendInvitePayload) => Promise<IApiResponse<IInvite>>;
    getInvite: (token: string) => Promise<IApiResponse<IInvite>>;
    acceptInvite: (
        token: string,
        payload: IAcceptInvitePayload
    ) => Promise<IApiResponse<any>>;
    deleteInvite: (token: string) => Promise<IApiResponse<any>>;
    getAllInvites: () => Promise<IApiResponse<IInvite[]>>;
    getUsers: () => Promise<IApiResponse<IUser[]>>;
    updateUser: (id: number, payload: { name: string; email: string; role_id: number }) => Promise<IApiResponse<any>>;
    deleteUser: (id: number) => Promise<IApiResponse<any>>;
    roles: () => Promise<IApiResponse<IRole[]>>;
    getAllApkVersions: () => Promise<IApiResponse<string[]>>;
    getSettings: () => Promise<IApiResponse<ISetting[]>>;
    updateSetting: (id: number, payload: { key: string; value: string }) => Promise<IApiResponse<ISetting>>;
}

export const useApiStore = defineStore<"api", IApiState, {}, IApiActions>(
    "api",
    {
        getters: {
            token() {
                const auth = useAuthStore();
                return auth.token;
            },

            api() {
                return axios.create({
                    baseURL: "/api/v1/admin",
                    headers: this.token ? { Authorization: `Bearer ${this.token}` } : {}
                });
            }
        },

        actions: {
            /** Users */

            /**
             * Attempt to login with the given credentials.
             * @param {string} email - email of user
             * @param {string} password - password of user
             * @returns Promise<IApiResponse<Login>>
             */
            login(email: string, password: string) {
                return this.api.post("/login", { email, password });
            },

            /**
             * Attempt to logout.
             * @param {boolean} everywhere - if 'true' logout everywhere, otherwise only in the current session.
             * @returns Promise<IApiResponse<[]>>
             */
            logout(everywhere: boolean = false) {
                return this.api.post("/logout", { everywhere });
            },

            /**
             * Attempt to get the current user.
             * @returns Promise<IApiResponse<Me>>
             */
            me() {
                return this.api.get("/me");
            },

            /** Blocks */

            /**
             * Attempt to get all blocks.
             * @returns Promise<IApiResponse<Block[]>>
             */
            blocks() {
                return this.api.get("/blocks");
            },

            /**
             * Attempt to create a new block.
             * @returns Promise<IApiResponse<any>>
             */
            createBlock() {
                return this.api.post("/blocks");
            },

            /**
             * Attempt to update an existing block.
             * @returns Promise<IApiResponse<any>>
             */
            updateBlock() {
                return this.api.patch("/blocks");
            },

            /**
             * Attempt to delete an existing block.
             * @returns Promise<IApiResponse<any>>
             */
            deleteBlock() {
                return this.api.delete("/blocks");
            },

            /** Block groups */
            blockGroups() {
                return this.api.get("/all-block-groups");
            },

            /** Programs */

            /**
             * Attempt to get all massage programs.
             * @returns Promise<IApiResponse<MassageProgram[]>>
             */
            massagePrograms() {
                return this.api.get("/massage-programs");
            },

            /**
             * Attempt to get a specific massage program by id.
             * @param {string} id - id of massage program
             * @returns Promise<IApiResponse<MassageProgram>>
             */
            getMassageProgram(id: string) {
                return this.api.get(`/massage-programs/${id}`);
            },
            /**
             * Attempt to create a new massage program.
             * @param {any} payload - payload of massage program
             * @returns Promise<IApiResponse<any>>
             */
            createMassageProgram(payload: any) {
                return this.api.post("/massage-programs", payload);
            },

            /**
             * Attempt to update an existing massage program.
             * @param {string} id - id of massage program
             * @param {any} payload - payload of massage program
             * @returns Promise<IApiResponse<any>>
             */
            updateMassageProgram(id: string, payload: any) {
                return this.api.patch(`/massage-programs/${id}`, payload);
            },

            /**
             * Attempt to delete an existing massage program.
             * @param {string} id - id of massage program
             * @returns Promise<IApiResponse<any>>
             */
            deleteMassageProgram(id: string) {
                return this.api.delete(`/massage-programs/${id}`);
            },

            /** Training programs */

            /**
             * Attempt to get all training programs.
             * @returns Promise<IApiResponse<TrainingProgram[]>>
             */
            trainingPrograms() {
                return this.api.get("/training-programs");
            },

            /**
             * Attempt to get a specific training program by id.
             * @param {string} id - id of training program
             * @returns Promise<IApiResponse<TrainingProgram>>
             */
            getTrainingProgram(id: string) {
                return this.api.get(`/training-programs/${id}`);
            },

            /**
             * Attempt to create a new training program.
             * @param {any} payload - payload of training program
             * @returns Promise<IApiResponse<any>>
             */
            createTrainingProgram(payload: any) {
                return this.api.post("/training-programs", payload);
            },

            /**
             * Attempt to update an existing training program.
             * @param {string} id - id of training program
             * @param {any} payload - payload of training program
             * @returns Promise<IApiResponse<any>>
             */
            updateTrainingProgram(id: string, payload: any) {
                return this.api.patch(`/training-programs/${id}`, payload);
            },

            /**
             * Attempt to delete an existing training program.
             * @param {string} id - id of training program
             * @returns Promise<IApiResponse<any>>
             */
            deleteTrainingProgram(id: string) {
                return this.api.delete(`/training-programs/${id}`);
            },

            /** Training categories  */

            /**
             * Attempt to get all training categories.
             * @returns Promise<IApiResponse<ITrainingCategory[]>>
             */
            trainingCategories() {
                return this.api.get("/training-categories");
            },

            /**
             * Attempt to get a specific training category by id.
             * @param {string} id - id of training category
             * @returns Promise<IApiResponse<ITrainingCategory>>
             */
            getTrainingCategory(id: string) {
                return this.api.get(`/training-categories/${id}`);
            },

            /**
             * Attempt to create a new training category.
             * @param {any} payload - payload of training category
             * @returns Promise<IApiResponse<any>>
             */
            createTrainingCategory(payload: any) {
                return this.api.post("/training-categories", payload);
            },

            /**
             * Attempt to update an existing training category.
             * @param {string} id - id of training category
             * @param {any} payload - payload of training category
             * @returns Promise<IApiResponse<any>>
             */
            updateTrainingCategory(id: string, payload: any) {
                return this.api.patch(`/training-categories/${id}`, payload);
            },

            /**
             * Attempt to delete an existing training category.
             * @param {string} id - id of training category
             * @returns Promise<IApiResponse<any>>
             */
            deleteTrainingCategory(id: string) {
                return this.api.delete(`/training-categories/${id}`);
            },

            addTrainingCategoryImage(id: string | number, payload: any) {
                const formData = new FormData();
                for (const key in payload) {
                    if (payload.hasOwnProperty(key)) formData.append(key, payload[key]);
                }
                return this.api.post(`/training-categories/${id}/image`, formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
            },

            deleteTrainingCategoryImage(id: string | number) {
                return this.api.delete(`/training-categories/${id}/image`);
            },

            /** Markets */

            /**
             * Attempt to get all markets.
             * @returns Promise<IApiResponse<Market[]>>
             */
            markets() {
                return this.api.get("/markets");
            },

            /**
             * Attempt to get a specific market by id.
             * @param {string} id - id of market
             */
            getMarket(id: string) {
                return this.api.get(`/markets/${id}`);
            },

            /**
             * Attempt to get the read-only content tree for a market.
             * @param {string} id - id of market
             */
            getMarketContentTree(id: string) {
                return this.api.get(`/markets/${id}/content-tree`);
            },

            /**
             * Attempt to create a new market.
             * @param {any} payload - payload of market
             * @returns Promise<IApiResponse<any>>
             */
            createMarket(payload: any) {
                return this.api.post("/markets", payload);
            },

            /**
             * Attempt to update an existing market.
             * @param {string} id - id of market
             * @param {any} payload - payload of market
             * @returns Promise<IApiResponse<any>>
             */
            updateMarket(id: string, payload: any) {
                return this.api.patch(`/markets/${id}`, payload);
            },

            /**
             * Attempt to delete an existing market.
             * @param {string} id - id of market
             * @returns Promise<IApiResponse<any>>
             */
            deleteMarket(id: string) {
                return this.api.delete(`/markets/${id}`);
            },

            /** Chairs */

            /**
             * Attempt to get all chairs.
             * @returns Promise<IApiResponse<Chair[]>>
             */
            chairs() {
                return this.api.get("/chairs");
            },

            /**
             * Attempt to get a specific chair by id.
             */
            getChair(id: string) {
                return this.api.get(`/chairs/${id}`);
            },

            /**
             * Attempt to update an existing chair.
             */
            updateChair(id: string, payload: any) {
                return this.api.patch(`/chairs/${id}`, payload);
            },

            /**
             * Attempt to delete an existing chair.
             */
            deleteChair(id: string) {
                return this.api.delete(`/chairs/${id}`);
            },

            /**
             * Attempt to set chair error to fixed
             */
            setChairErrorFixed(id: number) {
                return this.api.patch(`/error/fixed`, { error_id: id });
            },

            /**
             * Get event logs for a specific chair.
             */
            getChairEventLogs(id: string | number) {
                return this.api.get(`/chairs/${id}/event-logs`);
            },

            /**
             * Delete all event logs for a specific chair.
             */
            deleteChairEventLogs(id: string | number) {
                return this.api.delete(`/chairs/${id}/event-logs`);
            },

            /** Static pages */

            /**
             * Attempt to get all training programs.
             * @returns Promise<IApiResponse<IStaticPage[]>>
             */
            staticPages() {
                return this.api.get("/static-pages");
            },

            /**
             * Attempt to get a specific training program by id.
             * @param {string} id - id of training program
             * @returns Promise<IApiResponse<IStaticPage>>
             */
            getStaticPage(slug: string) {
                return this.api.get(`/static-pages/${slug}`);
            },

            /**
             * Attempt to update an existing static page.
             * @param {string} slug - slug of static page
             * @param {any} payload - payload of static page
             * @returns Promise<IApiResponse<any>>
             */
            updateStaticPage(slug: string, payload: any) {
                return this.api.patch(`/static-pages/${slug}`, payload);
            },

            /** Ui Components */

            /**
             * Attempt to get all ui component texts.
             * @returns Promise<IApiResponse<IUiComponent[]>>
             */
            uiComponents() {
                return this.api.get("/ui-components");
            },

            /**
             * Attempt to get a specific ui component by slug.
             * @param {string} slug - slug of ui component
             * @returns Promise<IApiResponse<IUiComponent>>
             */
            getUiComponent(slug: string) {
                return this.api.get(`/ui-components/${slug}`);
            },

            /**
             * Attempt to update an existing ui component.
             * @param {string} slug - slug of ui component
             * @param {any} payload - payload of ui component
             * @returns Promise<IApiResponse<any>>
             */
            updateUiComponent(slug: string, payload: any) {
                return this.api.patch(`/ui-components/${slug}`, payload);
            },

            /** Languages */

            /**
             * Attempt to get all languages.
             * @returns Promise<IApiResponse<Language[]>>
             */
            languages() {
                return this.api.get("/languages");
            },

            /**
             * Attempt to create a new language.
             * @returns Promise<IApiResponse<any>>
             */
            createLanguage() {
                return this.api.post("/languages");
            },

            /**
             * Attempt to update an existing language.
             * @returns Promise<IApiResponse<any>>
             */
            updateLanguage() {
                return this.api.patch("/languages");
            },

            /**
             * Attempt to delete an existing language.
             * @returns Promise<IApiResponse<any>>
             */
            deleteLanguage() {
                return this.api.delete("/languages");
            },

            /** Area tags */

            /**
             * Attempt to create a new area tag.
             * @param {any} payload - payload of area tag
             * @returns Promise<IApiResponse<IAreaTag>>
             */
            createAreaTag(payload: any) {
                return this.api.post("/area-tags", payload);
            },

            /**
             * Attempt to delete an existing area tag.
             * @param {number} id - id of area tag
             * @returns Promise<IApiResponse<IAreaTag>>
             */
            deleteAreaTag(id: number) {
                return this.api.delete(`/area-tags/${id}`);
            },

            /** Sellers */

            /**
             * Attempt to get all sellers.
             * @returns Promise<IApiResponse<ISeller[]>>
             */
            sellers() {
                return this.api.get("/sellers");
            },

            /**
             * Attempt to get seller by id
             * @returns Promise<IApiResponse<ISeller>>
             */
            getSeller(id: string) {
                return this.api.get(`/sellers/${id}`);
            },

            /**
             * Attempt to create a new seller.
             * @param {any} payload - payload of seller
             * @returns Promise<IApiResponse<ISeller>>
             */
            createSeller(payload: any) {
                return this.api.post("/sellers", payload);
            },

            /**
             * Attempt to update an existing seller.
             * @param {string} id - id of seller
             * @param {any} payload - payload of seller
             * @returns Promise<IApiResponse<ISeller>>
             */
            updateSeller(id: string, payload: any) {
                return this.api.patch(`/sellers/${id}`, payload);
            },

            /**
             * Attempt to delete an existing seller.
             * @param {string} id - id of seller
             * @returns Promise<IApiResponse<any>>
             */
            deleteSeller(id: string) {
                return this.api.delete(`/sellers/${id}`);
            },

            /** Customers */

            /**
             * Attempt to get all customers.
             * @returns Promise<IApiResponse<ICustomer[]>>
             */
            customers() {
                return this.api.get("/customers");
            },

            /**
             * Attempt to get a specific customer by id.
             * @param {string} id - id of customer
             */
            getCustomer(id) {
                return this.api.get(`/customers/${id}`);
            },

            /**
             * Attempt to create a new customer.
             * @param {any} payload - payload of customer
             * @returns Promise<IApiResponse<ICustomer>>
             */
            createCustomer(payload: any) {
                return this.api.post("/customers", payload);
            },

            /**
             * Attempt to update an existing customer.
             * @param {string} id - id of customer
             * @param {any} payload - payload of customer
             * @returns Promise<IApiResponse<any>>
             */
            updateCustomer(id: string, payload: any) {
                return this.api.patch(`/customers/${id}`, payload);
            },

            /**
             * Attempt to delete an existing customer.
             * @param {string} id - id of customer
             * @returns Promise<IApiResponse<any>>
             */
            deleteCustomer(id: string) {
                return this.api.delete(`/customers/${id}`);
            },

            /**
             * Get all portal customers, including which customer each one is
             * linked to (so taken ones can be shown but disabled).
             * @returns Promise<IApiResponse<IPortalCustomer[]>>
             */
            portalCustomers() {
                return this.api.get("/portal-customers");
            },

            /** Audio files */
            /**
             * Upload an audio file.
             * @param {IAudioFileUploadPayload} payload - Payload for uploading an audio file
             * @returns Promise<IApiResponse<IAudioFile>>
             */
            uploadAudio(
                payload: IAudioFileUploadPayload
            ): Promise<IApiResponse<IAudioFile>> {
                const formData = new FormData();
                for (const key in payload) {
                    if (payload.hasOwnProperty(key)) formData.append(key, payload[key]);
                }
                return this.api.post("/audio-files", formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
            },

            /**
             * Update an existing audio file.
             * @param {IAudioFileUpdatePayload} payload - Payload for updating an audio file
             * @returns Promise<IApiResponse<IAudioFile>>
             */
            updateAudio(
                payload: IAudioFileUpdatePayload
            ): Promise<IApiResponse<IAudioFile>> {
                return this.api.patch(`/audio-files/${payload.id}`, payload);
            },

            /**
             * Delete an existing audio file by ID.
             * @param {number} id - ID of the audio file to delete
             * @returns Promise<IApiResponse<IAudioFile>>
             */
            deleteAudio(id: number): Promise<IApiResponse<IAudioFile>> {
                return this.api.delete(`/audio-files/${id}`);
            },

            /** Sectors */

            /**
             * Attempt to get all sectors
             * @returns Promise<IApiResponse<ISector[]>>
             */
            sectors() {
                return this.api.get("/sectors");
            },

            /**
             * Attempt to create a new sector.
             * @param {any} payload - payload of sector
             * @returns Promise<IApiResponse<ISector>>
             */
            createSector(payload: any) {
                return this.api.post("/sectors", payload);
            },

            /** Segments */

            /**
             * Attempt to get all segments
             * @returns Promise<IApiResponse<ISegment[]>>
             */
            segments() {
                return this.api.get("/segments");
            },

            /**
             * Attempt to create a new segment.
             * @param {any} payload - payload of segment
             * @returns Promise<IApiResponse<ISegment>>
             */
            createSegment(payload: any) {
                return this.api.post("/segments", payload);
            },

            /** Invites */

            /**
             * Attempt to send invite.
             * @param {ISendInvitePayload} payload - payload of invite
             * @returns Promise<IApiResponse<IInvite>>
             */
            sendInvite(payload: ISendInvitePayload) {
                return this.api.post("/invites/send", payload);
            },

            /**
             * Attempt to get invite.
             * @param {string} token - token of invite
             * @returns Promise<IApiResponse<IInvite>>
             */
            getInvite(token: string) {
                return this.api.get(`/invites/token/${token}`);
            },
            /**
             * Attempt to accept invite.
             * @param {string} token - token of invite
             * @param {IAcceptInvitePayload} payload - payload of invite
             * @returns Promise<IApiResponse<any>>
             */
            acceptInvite(token: string, payload: IAcceptInvitePayload) {
                return this.api.post(`/invites/accept/${token}`, payload);
            },

            /** Attempt to delete invite
             * @param {string} token - token of invite
             * @returns Promise<IApiResponse<any>>
             */
            deleteInvite(token: string) {
                return this.api.delete(`/invites/delete/${token}`);
            },

            /**
             * Attempt to get all invites
             * @returns Promise<IApiResponse<IInvite[]>>
             */
            getAllInvites() {
                return this.api.get("/invites");
            },

            /**
             * Attempt to get all users.
             * @returns Promise<IApiResponse<IUser[]>>
             */
            getUsers() {
                return this.api.get("/users");
            },

            /**
             * Attempt to update a user.
             * @returns Promise<IApiResponse<any>>
             */
            updateUser(id, payload) {
                return this.api.patch(`/users/${id}`, payload);
            },

            /**
             * Attempt to delete a user.
             * @returns Promise<IApiResponse<any>>
             */
            deleteUser(id) {
                return this.api.delete(`/users/${id}`);
            },

            /**
             * Attempt to get all roles
             * @returns Promise<IApiResponse<IRole[]>>
             */
            roles() {
                return this.api.get("/invites/roles");
            },

            /**
             * Attempt to get all apk versions
             * @returns Promise<IApiResponse<string[]>>
             */
            getAllApkVersions() {
                return this.api.get("/apk-versions");
            },

            /** Settings */

            /**
             * Attempt to get all settings
             * @returns Promise<IApiResponse<ISetting[]>>
             */
            getSettings() {
                return this.api.get("/settings");
            },

            /**
             * Attempt to update a setting.
             * @param {number} id - id of setting
             * @param {{ key: string; value: string }} payload - payload of setting
             * @returns Promise<IApiResponse<ISetting>>
             */
            updateSetting(id: number, payload: { key: string; value: string }) {
                return this.api.patch(`/settings/${id}`, payload);
            }
        }
    }
);

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useApiStore, import.meta.hot));
}
